import chroma from 'chroma-js';
import Konva from 'konva';
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Group, Line } from 'react-konva';
import { useAppSelector } from '../../app/hooks';
import { FeatureEnum } from '../../services/data/impl/cube/cubeSlice';
import {
  isBlueberryFeature,
  isBlueberryFlowerFeature,
  mapToAlias,
} from '../../services/data/impl/cube/cubeUtils';
import { PointDetection } from '../../services/data/impl/wrangler/schemas/pointDetections';
import { useFeatureFlags } from '../../services/featureFlags/hooks/useFeatureFlags';
import { isFeatureEnum } from '../commonUtils';
import { ImageScaleParams } from './ImageController';
import {
  selectDisplayUnknownMasks,
  selectImageMaskVisibility,
  selectMaskColors,
  selectMaskInstanceColors,
  selectMaskOpacity,
  selectMaskOutline,
} from './imageSlice';
import { MaskTooltipInfo } from './ImageStage';
import { scaledPoints } from './utils/imageUtils';

const DEFAULT_MASK_COLOR = '#fff';
const MASK_HIGHLIGHT_COLOR = '#ff0';

interface Props {
  detections: PointDetection;
  imageScaleParams: ImageScaleParams;
  imageWidth: number;
  imageHeight: number;
  setMaskTooltipInfo: Dispatch<SetStateAction<MaskTooltipInfo>>;
}

const Masks: React.FC<Props> = ({
  detections,
  imageScaleParams,
  imageWidth,
  imageHeight,
  setMaskTooltipInfo,
}) => {
  // Use selectors and states first without any conditional rendering
  const maskColors = useAppSelector(selectMaskColors);
  const maskOutline = useAppSelector(selectMaskOutline);
  const maskOpacity = useAppSelector(selectMaskOpacity);
  const imageMaskVisibility = useAppSelector(selectImageMaskVisibility);
  const displayUnknownMasks = useAppSelector(selectDisplayUnknownMasks);
  const maskInstanceColors = useAppSelector(selectMaskInstanceColors);

  const { masks, class_name, scores, distances, point_id } = detections;
  const classNameRaw = class_name ? class_name : '';
  const isValidFeature = isFeatureEnum(classNameRaw);

  const { Diagnostics } = useFeatureFlags();

  // Use the alias of the class name if it is a valid feature, otherwise use FeatureEnum.none.  Don't use aliases for blueberry features because every color aliases to "blueberry".
  let classNameAlias = FeatureEnum.none;
  if (isValidFeature) {
    classNameAlias =
      isBlueberryFeature(classNameRaw) || isBlueberryFlowerFeature(classNameRaw)
        ? classNameRaw
        : mapToAlias(classNameRaw);
  }

  const maskColor = isValidFeature
    ? maskColors[classNameAlias]
    : DEFAULT_MASK_COLOR;

  const uniqueColorPalette = useMemo(() => {
    if (maskInstanceColors) {
      return chroma
        .scale(chroma.brewer.Spectral)
        .mode('lch')
        .colors(masks.length);
    } else {
      return masks.map(() => maskColor);
    }
  }, [maskInstanceColors, masks, maskColor]);

  // Initial state for maskGroupColors
  const [maskGroupColorsState, setMaskGroupColorsState] =
    useState<string[]>(uniqueColorPalette);

  useEffect(() => {
    setMaskGroupColorsState(uniqueColorPalette);
  }, [uniqueColorPalette]);

  const handleMouseEnter = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent>, groupIndex: number) => {
      const stage = e.target.getStage();
      const pointerPosition = stage?.getPointerPosition();
      if (pointerPosition) {
        setMaskTooltipInfo({
          visible: true,
          mousePosX: pointerPosition.x,
          mousePosY: pointerPosition.y,
          feature: classNameAlias,
          score: scores && scores[groupIndex] ? scores[groupIndex] : '',
          distance:
            distances && distances[groupIndex] ? distances[groupIndex][0] : '',
          model: detections.model_name,
          className: detections.class_name,
          modelVersion: detections.model_version,
        });
      }

      // Set the color of the mask on mouse enter
      const newMaskColors = [...maskGroupColorsState];
      newMaskColors[groupIndex] = MASK_HIGHLIGHT_COLOR;
      setMaskGroupColorsState(newMaskColors);
    },
    [
      maskGroupColorsState,
      setMaskTooltipInfo,
      classNameAlias,
      scores,
      distances,
      detections.model_name,
      detections.class_name,
      detections.model_version,
    ]
  );

  const handleMouseLeave = useCallback(
    (groupIndex: number) => {
      setMaskTooltipInfo((prev) => ({ ...prev, visible: false }));
      // Reset the color of the hovered mask
      const newMaskColors = [...maskGroupColorsState];
      newMaskColors[groupIndex] = uniqueColorPalette[groupIndex];
      setMaskGroupColorsState(newMaskColors);
    },
    [maskGroupColorsState, setMaskTooltipInfo, uniqueColorPalette]
  );

  const maskElements = useMemo(
    () =>
      masks.map((maskGroup, groupIndex) =>
        maskGroup.map((mask, maskIndex) => {
          const points = scaledPoints(
            mask,
            imageScaleParams,
            imageWidth,
            imageHeight
          );

          return (
            <React.Fragment
              key={`mask-group-${groupIndex}-mask-${maskIndex}-${point_id}`}
            >
              {maskOutline && (
                <Line
                  key={`mask-group-${groupIndex}-mask-${maskIndex}-stroke`}
                  points={points}
                  closed={true}
                  stroke={maskGroupColorsState[groupIndex]}
                  strokeWidth={1}
                  tension={0.5} // smooths the line
                  onMouseEnter={(e) => handleMouseEnter(e, groupIndex)}
                  onMouseLeave={() => handleMouseLeave(groupIndex)}
                />
              )}
              <Line
                key={`mask-group-${groupIndex}-mask-${maskIndex}-fill`}
                points={points}
                closed={true}
                fill={maskGroupColorsState[groupIndex]}
                opacity={maskOpacity / 100}
                tension={0.5} // smooths the line
                onMouseEnter={(e) => handleMouseEnter(e, groupIndex)}
                onMouseLeave={() => handleMouseLeave(groupIndex)}
              />
            </React.Fragment>
          );
        })
      ),
    [
      masks,
      imageScaleParams,
      imageWidth,
      imageHeight,
      point_id,
      maskOutline,
      maskGroupColorsState,
      maskOpacity,
      handleMouseEnter,
      handleMouseLeave,
    ]
  );

  // Return null if conditions aren't met
  if (isValidFeature && !imageMaskVisibility[classNameAlias]) {
    return null;
  }

  // Hide invalid masks unless displayUnknownMasks is enabled with Diagnostics
  if (!isValidFeature && !displayUnknownMasks && Diagnostics) {
    return null;
  }

  return (
    <>
      <Group
        clipFunc={(ctx) => {
          ctx.rect(
            imageScaleParams.x,
            imageScaleParams.y,
            imageWidth * imageScaleParams.scale,
            imageHeight * imageScaleParams.scale
          );
        }} // clip the mask to the image size so that the mask doesn't extend beyond the image (the line smoothing function can cause the mask to extend beyond the image)
      >
        {maskElements}
      </Group>
    </>
  );
};

export default Masks;
